import React, { useState, useEffect } from 'react';
import { Share, Plus, X } from 'lucide-react';
import { isIOS, isStandalone as isInStandaloneMode } from '../utils/installPromptStore';

/**
 * Floating install prompt — iOS-only.
 *
 * On Android/Chrome we intentionally do NOT preventDefault the
 * `beforeinstallprompt` event from the app shell, so Chrome's native install
 * banner can show without producing a "Banner not shown" console warning.
 * The marketing landing page handles its own custom Install button when the
 * user is there explicitly.
 */
const InstallPrompt = () => {
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [iosStep, setIosStep] = useState(0);

  useEffect(() => {
    if (isInStandaloneMode()) return;
    if (!isIOS()) return;

    // Honor 7-day dismissal
    const dismissedAt = localStorage.getItem('installPromptDismissed');
    if (dismissedAt) {
      const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      if (parseInt(dismissedAt) > sevenDaysAgo) return;
    }

    const timer = setTimeout(() => setShowIOSGuide(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setShowIOSGuide(false);
    localStorage.setItem('installPromptDismissed', Date.now().toString());
  };

  if (!showIOSGuide) return null;

  const steps = [
    {
      icon: <Share className="w-6 h-6 text-blue-400" />,
      title: 'Tap the Share button',
      desc: 'Find it at the bottom of Safari (the square with an arrow pointing up)',
    },
    {
      icon: <Plus className="w-6 h-6 text-white bg-stone-700 rounded p-0.5" />,
      title: 'Tap "Add to Home Screen"',
      desc: 'Scroll down in the share menu until you see it',
    },
    {
      icon: <span className="text-lg font-bold text-blood-400">Go</span>,
      title: 'Tap "Add"',
      desc: 'Anvil & Ash will appear on your home screen like a real app',
    },
  ];

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 animate-slide-up">
      <div className="bg-stone-900 border border-stone-700 rounded-xl shadow-2xl overflow-hidden" data-testid="ios-install-guide">
        <div className="bg-gradient-to-r from-blood-700 to-blood-900 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/icon-72x72.png" alt="Anvil & Ash" className="w-8 h-8 rounded-lg" />
            <span className="text-white text-sm font-bold tracking-wider">INSTALL ON iPHONE</span>
          </div>
          <button onClick={handleDismiss} className="text-white/60 hover:text-white" data-testid="ios-guide-dismiss">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-stone-800 border border-stone-600 flex items-center justify-center flex-shrink-0">
              {steps[iosStep].icon}
            </div>
            <div>
              <p className="text-white text-sm font-bold">{steps[iosStep].title}</p>
              <p className="text-stone-400 text-xs mt-0.5">{steps[iosStep].desc}</p>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mb-4">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${i === iosStep ? 'bg-blood-500' : i < iosStep ? 'bg-blood-800' : 'bg-stone-700'}`}
              />
            ))}
          </div>

          <div className="flex gap-2">
            {iosStep < steps.length - 1 ? (
              <button
                onClick={() => setIosStep(iosStep + 1)}
                className="flex-1 bg-blood-600 hover:bg-blood-700 text-white text-xs font-bold py-2.5 px-4 rounded-lg transition-colors"
                data-testid="ios-guide-next"
              >
                Next Step
              </button>
            ) : (
              <button
                onClick={handleDismiss}
                className="flex-1 bg-blood-600 hover:bg-blood-700 text-white text-xs font-bold py-2.5 px-4 rounded-lg transition-colors"
                data-testid="ios-guide-done"
              >
                Got It
              </button>
            )}
            <button
              onClick={handleDismiss}
              className="text-stone-500 hover:text-stone-300 text-xs px-3 transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
